import { cx } from '@linaria/atomic';
import type { PolymorphicComponentPropWithoutRef } from '../../../type-utils/polymorphic';
import { Button } from '../../button/button.component';
import { ClickHandler, withClose } from '../atoms/helpers';
import { useModalContext } from '../provider/modal.provider';
import { trayActions, trayActionsRightContent, trayActionsWithOnlyRightContent } from './tray.styles';

type ComponentProps = {
  disabledPrimary?: boolean;
  onPrimaryClick?: ClickHandler;
  primaryLabel?: string;
  disabledSecondary?: boolean;
  onSecondaryClick?: ClickHandler;
  secondaryLabel?: string;
  primaryTrackingId?: string;
  secondaryTrackingId?: string;
  tertiaryLabel?: string;
  onTertiaryClick?: ClickHandler;
  tertiaryTrackingId?: string;
  tertiaryIsDestructive?: boolean;
};

type TrayActionsProps<T extends React.ElementType> = PolymorphicComponentPropWithoutRef<T, ComponentProps>;

export const TrayActions = <T extends React.ElementType = 'section'>({
  as,
  disabledPrimary = false,
  onPrimaryClick,
  primaryLabel,
  primaryTrackingId,
  disabledSecondary = false,
  onSecondaryClick,
  secondaryLabel,
  secondaryTrackingId,
  tertiaryLabel,
  onTertiaryClick,
  tertiaryTrackingId,
  tertiaryIsDestructive = false,
  className,
  ...rest
}: TrayActionsProps<T>) => {
  const Component = as || 'section';
  const { onClose } = useModalContext();

  return (
    <Component
      className={cx(trayActions, !onTertiaryClick ? trayActionsWithOnlyRightContent : undefined, className)}
      {...rest}
    >
      {onTertiaryClick && (
        <Button
          variant='tertiary'
          className='tray-tertiary-button'
          trackingId={tertiaryTrackingId}
          onClick={withClose(onClose, onTertiaryClick)}
          size='large'
          destructive={!!tertiaryIsDestructive}
        >
          {tertiaryLabel}
        </Button>
      )}
      <div className={trayActionsRightContent}>
        {onSecondaryClick && (
          <Button
            variant='secondary'
            className='tray-secondary-button'
            trackingId={secondaryTrackingId}
            disabled={disabledSecondary}
            onClick={withClose(onClose, onSecondaryClick)}
            size='large'
          >
            {secondaryLabel || 'Cancel'}
          </Button>
        )}
        <Button
          className='tray-primary-button'
          trackingId={primaryTrackingId}
          disabled={disabledPrimary}
          onClick={withClose(onClose, onPrimaryClick)}
          size='large'
        >
          {primaryLabel || 'Ok'}
        </Button>
      </div>
    </Component>
  );
};
